import React, { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, Truck, Upload, CalendarRange, Wallet, MinusCircle,
  FileText, CalendarClock, Plane, ShieldCheck, BarChart3, Building2,
  Plug, Settings, LogOut, Menu, FileSignature, Gavel, Fuel, Bell,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";

const NAV_ITEMS = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true, roles: ["platform_owner", "company_admin", "depot_manager"] },
  { to: "/admin/drivers", icon: Users, label: "Drivers", roles: ["platform_owner", "company_admin", "depot_manager"] },
  { to: "/admin/vehicles", icon: Truck, label: "Vehicles", roles: ["platform_owner", "company_admin", "depot_manager"] },
  { to: "/admin/dpd-import", icon: Upload, label: "DPD Import", roles: ["platform_owner", "company_admin"] },
  { to: "/admin/pay-periods", icon: CalendarRange, label: "Pay Periods", roles: ["platform_owner", "company_admin"] },
  { to: "/admin/deductions", icon: MinusCircle, label: "Deductions", roles: ["platform_owner", "company_admin"] },
  { to: "/admin/penalties", icon: Gavel, label: "Penalties", roles: ["platform_owner", "company_admin", "depot_manager"] },
  { to: "/admin/fuel", icon: Fuel, label: "Fuel Receipts", roles: ["platform_owner", "company_admin", "depot_manager"] },
  { to: "/admin/notifications", icon: Bell, label: "Notification Chat", roles: ["platform_owner", "company_admin", "depot_manager"] },
  { to: "/admin/invoices", icon: FileText, label: "Invoices", roles: ["platform_owner", "company_admin"] },
  { to: "/admin/holidays", icon: Plane, label: "Holidays", roles: ["platform_owner", "company_admin", "depot_manager"] },
  { to: "/admin/documents", icon: ShieldCheck, label: "Documents", roles: ["platform_owner", "company_admin", "depot_manager"] },
  { to: "/admin/contracts", icon: FileSignature, label: "Contracts", roles: ["platform_owner", "company_admin"] },
  { to: "/admin/business-protection", icon: ShieldCheck, label: "Business Protection", roles: ["platform_owner", "company_admin"] },
  { to: "/admin/users", icon: Users, label: "Users", roles: ["platform_owner", "company_admin"] },
  { to: "/admin/companies", icon: Building2, label: "Companies", roles: ["platform_owner"] },
  { to: "/admin/work-planner", icon: CalendarClock, label: "Work Planner", roles: ["platform_owner", "company_admin", "depot_manager"] },
  { to: "/admin/reports", icon: BarChart3, label: "Reports", disabled: true, roles: ["platform_owner", "company_admin"] },
  { to: "/admin/integrations/dpd", icon: Plug, label: "DPD Portal Sync", roles: ["platform_owner", "company_admin"] },
  { to: "/admin/settings", icon: Settings, label: "Company Settings", roles: ["platform_owner", "company_admin"] },
  { to: "/admin/profile", icon: Settings, label: "My Profile", roles: ["platform_owner", "company_admin", "depot_manager"] },
];

/** Shared nav list used by both desktop sidebar and mobile drawer. */
const NavList = ({ items, onNavigate }) => (
  <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5" data-testid="admin-nav">
    {items.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.exact}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        onClick={(e) => {
          if (item.disabled) {
            e.preventDefault();
            return;
          }
          onNavigate?.();
        }}
        className={({ isActive }) =>
          `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-150 ${
            item.disabled
              ? "text-brand-text-muted/50 cursor-not-allowed"
              : isActive
              ? "bg-brand-navy text-white"
              : "text-brand-text-muted hover:bg-brand-surface hover:text-brand-text"
          }`
        }
        title={item.disabled ? "Coming soon" : item.label}
      >
        <item.icon className="w-4 h-4" />
        <span className="flex-1">{item.label}</span>
        {item.disabled && (
          <span className="text-[9px] uppercase tracking-wider text-brand-text-muted/60">soon</span>
        )}
      </NavLink>
    ))}
  </nav>
);

const RoleBadge = ({ role }) => {
  const map = {
    platform_owner: { label: "Platform Owner", cls: "bg-brand-red/10 text-brand-red border-brand-red/20" },
    company_admin: { label: "Company Admin", cls: "bg-brand-navy/10 text-brand-navy border-brand-navy/20" },
    depot_manager: { label: "Depot Manager", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  };
  const cfg = map[role] || { label: role, cls: "bg-brand-surface text-brand-text-muted" };
  return (
    <span className={`inline-block text-[9px] font-mono uppercase tracking-[0.15em] px-1.5 py-0.5 rounded border ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const nav = NAV_ITEMS.filter((n) => n.roles.includes(user?.role));

  // Auto-close drawer on route change (safety)
  React.useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const sidebarInner = (
    <>
      <div className="px-6 py-6 border-b border-brand-border">
        <div className="flex items-start justify-between gap-3">
          <Logo variant="full" size="md" />
          <NotificationBell />
        </div>
        <div className="mt-3">
          <RoleBadge role={user?.role} />
        </div>
      </div>
      <NavList items={nav} onNavigate={() => setDrawerOpen(false)} />
      <div className="p-4 border-t border-brand-border">
        <div className="text-xs text-brand-text-muted mb-2">
          <div className="font-medium text-brand-text" data-testid="admin-user-name">{user?.full_name}</div>
          <div className="text-[10px] mt-0.5 opacity-70">{user?.email}</div>
        </div>
        <Button
          data-testid="admin-logout-btn"
          variant="outline"
          className="w-full justify-start gap-2 border-brand-border"
          onClick={logout}
        >
          <LogOut className="w-4 h-4" /> Log out
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-brand-surface text-brand-text flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 h-screen sticky top-0 border-r border-brand-border bg-white">
        {sidebarInner}
      </aside>

      <main className="flex-1 min-w-0">
        {/* Mobile header (hamburger + logo + logout) — respects iOS safe area */}
        <header
          className="md:hidden sticky top-0 z-40 flex items-center justify-between px-4 h-14 bg-white border-b border-brand-border"
          style={{ paddingTop: "env(safe-area-inset-top)",
                    height: "calc(3.5rem + env(safe-area-inset-top))" }}
        >
          <div className="flex items-center gap-2">
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="-ml-2"
                  data-testid="admin-menu-btn"
                  aria-label="Open menu"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="p-0 w-72 flex flex-col bg-white border-r border-brand-border"
                data-testid="admin-menu-drawer"
              >
                <SheetTitle className="sr-only">Admin navigation</SheetTitle>
                <SheetDescription className="sr-only">Sidebar navigation for MY PORTAL admin.</SheetDescription>
                {sidebarInner}
              </SheetContent>
            </Sheet>
            <Logo variant="full" size="sm" />
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <Button variant="ghost" size="sm" data-testid="admin-logout-btn-mobile" onClick={logout} aria-label="Log out">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </header>
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
